import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Loader2 } from 'lucide-react';
import useCards from '../hooks/useCards';
import Toast from '../components/utils/Toast';

// Add type for the result
interface ReadingResult {
    _id: string;
    // Add other properties as needed
}

const ReadingCards: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [stepMessage, setStepMessage] = useState<string>('');
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('error');
    // Use a ref to track if the fetch has started, preventing duplicate requests
    const fetchStarted = useRef(false);

    // Get the required API methods from the recipe context
    const { getPhotos, getCards, getTextTarotReading, getPhotoTarotReading } = useCards();

    useEffect(() => {
        const fetchData = async () => {
            let result: ReadingResult | undefined;
            // Return early if fetch has already started to prevent duplicate requests
            if (fetchStarted.current) return;
            // Mark fetch as started before proceeding
            fetchStarted.current = true;

            try {
                const photos = await getPhotos();
                const metadata = JSON.parse(localStorage.getItem('drawnCards') || '{}');

                let result: any;

                setStepMessage(t('readingCards.readingCards'));
                if (photos.length > 0) {
                    result = await getPhotoTarotReading(metadata);
                } else {
                    result = await getTextTarotReading(metadata);

                }

                if (!result) {
                    throw new Error('Failed to get reading result');
                }


                if (!result._id) {
                    throw new Error('Failed to get reading result');
                }
                console.log('result', result);
                navigate('/reading/' + result._id);
            } catch (error: any) {
                console.error('Error during tarot reading:', error);

                // Check if it's an unauthorized error
                if (error?.response?.status === 401 || error?.status === 401) {
                    setToastMessage(t('readingCards.sessionExpired'));
                    setToastSeverity('error');
                    setToastOpen(true);
                    // The redirect will be handled by the CardsContext
                } else {
                    setToastMessage(t('readingCards.errorReading'));
                    setToastSeverity('error');
                    setToastOpen(true);
                }
            }
        };

        console.log("mounted");
        fetchData();

        return () => { };
    }, []);

    return (
        <>
            <div className="flex flex-col items-center justify-center text-[#42403C] min-h-screen y-overflow-hidden pb-[159px]">
                <div className="relative w-24 h-24 animate-spin">
                    <Loader2 className="w-24 h-24 text-[#42403C]" />
                </div>
                <div className="mt-4 text-xl font-semibold text-[#42403C]">
                    {t('readingCards.readingFortune')}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{stepMessage}</div>
                <div className="mt-8 flex space-x-2">
                    <div
                        className="w-3 h-3 bg-[#42403C] rounded-full animate-bounce"
                        style={{ animationDelay: '0s' }}
                    ></div>
                    <div
                        className="w-3 h-3 bg-[#42403C] rounded-full animate-bounce"
                        style={{ animationDelay: '0.2s' }}
                    ></div>
                    <div
                        className="w-3 h-3 bg-[#42403C] rounded-full animate-bounce"
                        style={{ animationDelay: '0.4s' }}
                    ></div>
                </div>
            </div>

            <Toast
                open={toastOpen}
                message={toastMessage}
                severity={toastSeverity}
                onClose={() => setToastOpen(false)}
            />
        </>
    );
};

export default ReadingCards; 